// @flow

import * as React from 'react';

import { ResponsiveImage } from '@ta/media.image';
import classNames from '@ta/common.classnames';
import { type Photo } from './photo-carousel';

import styles from './Photostrip.less';

/** Component props for a <PhotoCarousel> */
export type Props = {|
  /** The list of photo sources */
  photos: Array<Photo>,

  /** Index of the photo to be selected */
  selectedIndex?: number,

  /** Optional callback for when the carousel displays a new photo */
  onPhotoSelected?: number => void,

  /** Number of thumbnails to be displayed in a single row */
  numItemsInRow?: number,
|};

type State = {|
  currentIndex: number,
|};

class Photostrip extends React.Component<Props, State> {
  static getDerivedStateFromProps({ selectedIndex, photos }: Props, { currentIndex }: State) {
    if (selectedIndex === undefined) {
      return null;
    }

    const lastIndex = photos.length - 1;
    if (selectedIndex !== currentIndex) {
      return {
        currentIndex: Math.max(Math.min(selectedIndex, lastIndex), 0),
      };
    }

    return null;
  }

  state = {
    currentIndex: 0,
  };

  onPhotoClick = (currentIndex: number) => {
    this.setState({ currentIndex });

    if (this.props.onPhotoSelected) {
      this.props.onPhotoSelected(currentIndex);
    }
  };

  render() {
    if (!this.props.photos.length) {
      return null;
    }

    const currentIndex = Math.max(
      Math.min(this.state.currentIndex, this.props.photos.length - 1),
      0,
    );

    // Default to showing all thumbnails in a single row
    const numItemsInRow = this.props.numItemsInRow
      ? this.props.numItemsInRow
      : this.props.photos.length;
    const eachItemWidth = numItemsInRow !== 0 ? 1 / numItemsInRow : 0;
    return (
      <div className={styles.photostrip}>
        {this.props.photos.map((photo, index) => (
          <div
            className={classNames(
              styles.stripItem,
              currentIndex === index ? styles.selected : null,
            )}
            style={{ flexBasis: `calc(${eachItemWidth * 100 - 0.01}% - 2px)` }} // -0.01 to avoid an Edge 42 bug
            onClick={() => this.onPhotoClick(index)}
            key={photo.id}
          >
            <ResponsiveImage sources={photo.photoSizes} height="100%" width="100%" />
          </div>
        ))}
      </div>
    );
  }
}

export default Photostrip;
